import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { StoreProvider, useStore } from './store/StoreContext.jsx';
import { DEV_ONLY } from './env.js';
import { LayoutProvider, useLayout } from './store/LayoutContext.jsx';
import { useAutoCalcFields } from './store/useAutoCalcFields.js';
import Topbar from './components/Topbar.jsx';
import PQScreen from './screens/PQScreen.jsx';
import { tabForSection } from './schema/pqTabs.js';
import PresentationScreen from './screens/PresentationScreen.jsx';
import PlanScreen from './screens/PlanScreen.jsx';
import PlanningScreen from './screens/PlanningScreen.jsx';
import AssessmentScreen from './screens/AssessmentScreen.jsx';
import ConversationScreen from './screens/ConversationScreen.jsx';
import DataConsole from './components/DataConsole.jsx';
import UndoToast from './components/UndoToast.jsx';
import { SAVE_MSG_VALIDATION, SAVE_MSG_SYSTEM, saveMsgQuarantined } from './utils/validation.js';

const SCREENS = {
  PQ: 'pq',
  PRESENTATION: 'presentation',
  PLAN: 'plan',
  PLANNING: 'planning',
  ASSESSMENT: 'assessment',
  BRIEF: 'brief',
  WORKBENCH: 'workbench',
  FOUNDATION: 'foundation',
  ASSESSMENT_BRIEF: 'assessment-brief',
};

// The assessment ships on dev, local dev and the public demo. The first two are
// DEV_ONLY (src/env.js); the demo is a production build that sets VITE_DEMO
// instead, so it has to ask for itself. Staging and production get neither:
// staging carries only what is headed to production (2026-10-20), and keeping
// it out of `main` is what stops a promoted PQ fix dragging the assessment
// along with it.
//
// The demo is safe to open on purpose: it carries no Salesforce connection and
// DEMO_MODE forces the sample household, so there is no real record behind it.
// It exists for reviewers who cannot authenticate through Okta.
const ASSESSMENT_ENABLED = DEV_ONLY || import.meta.env.VITE_DEMO === 'true';

// The assessment IS the redesign now. `?classic=1` still reaches the report it
// replaced, because that one carries the browser suite proving the household's
// figures and is worth keeping reachable while the redesign finishes its
// takeaway. The escape hatch goes when the older report does.
const CLASSIC_ASSESSMENT =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('classic') === '1';
const REDESIGNED_ASSESSMENT = !CLASSIC_ASSESSMENT;

// The planning workspace is held back from production for the same reason, but
// a different cause: its change log and the handoff snapshot it measures
// against are stored in Salesforce fields (planningChangeLog, pqSnapshot) that
// do not exist there yet. Shipping it would let a planner work a PQ, show the
// edits as saved, and write them into nothing. Flip this on once Srivani has
// created both fields (Jimmy, 2026-08-19). Dev only since 2026-10-20, like the
// assessment: planners see it on dev, against test records.
const PLANNING_ENABLED = DEV_ONLY;

// The Foundation Plan prototype (Jimmy, 2026-10-07). It has no backend yet, so
// it is held to local development, tests and the demo build, which is stricter
// than PREPROD: pq-dev and staging do not show it. __FOUNDATION_BUILD__ is a
// compile-time constant from vite.config.js, so production never ships it: the
// lazy chunk, the invented households inside it and every `fnd-` class are
// dropped from that build.
// eslint-disable-next-line no-undef
const FOUNDATION_ENABLED = __FOUNDATION_BUILD__;
const FoundationRoot = FOUNDATION_ENABLED ? lazy(() => import('./foundation/FoundationRoot.jsx')) : null;
const FOUNDATION_SCREENS = [SCREENS.BRIEF, SCREENS.WORKBENCH, SCREENS.FOUNDATION];

// The AI planning brief (Jimmy, 2026-10-12, after Joe and Jake's 10/8 review):
// Claude drafts it from the PQ, the meeting notes and the documents, and the
// advisor edits it. It needs the Function's Claude route, which only pq-dev
// and a local Function have, so it is held to dev (vite.config.js); the public
// demo build has no server and never offers it. A lazy chunk, so its fonts,
// styles and portrait print setup load only when it is opened.
// __BRIEF_BUILD__ is a compile-time constant from vite.config.js, so
// production and the demo build leave the chunk out altogether.
// eslint-disable-next-line no-undef
const ASSESSMENT_BRIEF_ENABLED = __BRIEF_BUILD__;
const BriefScreen = ASSESSMENT_BRIEF_ENABLED ? lazy(() => import('./brief/BriefScreen.jsx')) : null;

// Map the URL path to a screen. The ?pqid= query string is independent of
// the path and is preserved across navigation.
function screenFromPath(pathname) {
  const p = pathname.replace(/\/+$/, '').toLowerCase();
  // None of the prototype's three paths ends with an existing path ('/plan',
  // '/planning', '/assessment', '/present'), so endsWith cannot collide.
  // '/assessment-brief' ends with '-brief', not '/brief', so it cannot be
  // mistaken for the Foundation Plan's brief below.
  if (ASSESSMENT_BRIEF_ENABLED && p.endsWith('/assessment-brief')) return SCREENS.ASSESSMENT_BRIEF;
  if (FOUNDATION_ENABLED && p.endsWith('/brief')) return SCREENS.BRIEF;
  if (FOUNDATION_ENABLED && p.endsWith('/workbench')) return SCREENS.WORKBENCH;
  if (FOUNDATION_ENABLED && p.endsWith('/foundation')) return SCREENS.FOUNDATION;
  // Order matters: '/planning' also ends with a path the PLAN check would not
  // catch, but keep the more specific test first for clarity.
  if (PLANNING_ENABLED && p.endsWith('/planning')) return SCREENS.PLANNING;
  if (p.endsWith('/plan')) return SCREENS.PLAN;
  if (p.endsWith('/present') || p.endsWith('/presentation')) return SCREENS.PRESENTATION;
  if (ASSESSMENT_ENABLED && p.endsWith('/assessment')) return SCREENS.ASSESSMENT;
  return SCREENS.PQ;
}

const PATH_BY_SCREEN = {
  [SCREENS.PQ]: '/',
  [SCREENS.PLAN]: '/plan',
  [SCREENS.PRESENTATION]: '/present',
  [SCREENS.PLANNING]: '/planning',
  [SCREENS.ASSESSMENT]: '/assessment',
  [SCREENS.BRIEF]: '/brief',
  [SCREENS.WORKBENCH]: '/workbench',
  [SCREENS.FOUNDATION]: '/foundation',
  [SCREENS.ASSESSMENT_BRIEF]: '/assessment-brief',
};

// The prototype's three screens mount outside StoreProvider, the way the trade
// ticket mounts outside App (main.jsx). Inside it they would share the PQ's
// store: its Salesforce load, its autosave loop and its cross-tab write lock,
// so a /workbench tab opened first could take the lock from a real PQ tab in
// the same browser and leave that tab unable to save. Out here none of that
// machinery exists, which makes "a foundation screen never fires the PQ
// autosave" true by construction rather than by care. Moving between the
// three screens stays inside the prototype; leaving it for a PQ screen is a
// full page load, so the PQ starts with its store exactly as it always does.
function FoundationShell() {
  const [screen, setScreen] = useState(() => screenFromPath(window.location.pathname));

  const navigate = useCallback(nextScreen => {
    const url = (PATH_BY_SCREEN[nextScreen] || '/') + window.location.search;
    if (FOUNDATION_SCREENS.includes(nextScreen)) {
      window.history.pushState({}, '', url);
      // pushState keeps the scroll position, so a screen opened from far down
      // another one would open scrolled down; a new screen starts at its top.
      window.scrollTo(0, 0);
      setScreen(nextScreen);
    } else {
      window.location.assign(url);
    }
  }, []);

  useEffect(() => {
    const onPop = () => {
      const next = screenFromPath(window.location.pathname);
      if (FOUNDATION_SCREENS.includes(next)) setScreen(next);
      else window.location.reload();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return (
    <Suspense fallback={<div className="fnd-loading">Loading the Foundation Plan prototype</div>}>
      <FoundationRoot screen={screen} navigate={navigate} />
    </Suspense>
  );
}

function AppShell() {
  // Runs derived-field calculations on every store change. Lives inside the
  // provider so it can use useStore via useAutoCalcFields.
  useAutoCalcFields();

  const [screen, setScreen] = useState(() => screenFromPath(window.location.pathname));
  const [consoleOpen, setConsoleOpen] = useState(false);
  const { activeTab, setActiveTab } = useLayout();
  const {
    loading, loadError, saveError, saveErrorKind, noReference,
    restoredLocalEdits, coedit, readOnly, householdMode, quarantined, loadLatest,
  } = useStore();
  // 'idle' | 'loading' | 'failed', for the stale banner's button.
  const [latestState, setLatestState] = useState('idle');
  const onLoadLatest = useCallback(async () => {
    setLatestState('loading');
    const res = await loadLatest();
    setLatestState(res.ok ? 'idle' : 'failed');
  }, [loadLatest]);

  // Navigate: update the URL (preserving query string) and the screen.
  const navigate = useCallback(nextScreen => {
    const path = PATH_BY_SCREEN[nextScreen] || '/';
    const url = path + window.location.search;
    window.history.pushState({}, '', url);
    setScreen(nextScreen);
  }, []);

  // Keep screen in sync with browser back/forward.
  useEffect(() => {
    const onPop = () => setScreen(screenFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Scroll the first problem field into view from the save-error banner (R14):
  // the banner sits at the top of the page, so the actionable field can be a
  // long way down, and since the PQ became tabs it can be on another page
  // altogether. Only the open page's sections are mounted, so the jump first
  // works out which page the problem is on and turns to it.
  //
  // Which page: a missing name is on the household page; a quarantined value's
  // key starts with its section id (assets.realEstate.0.yearAcquired), which
  // names the page; a validation error has no path, so it can only be found
  // on the open page.
  const tabForError = useCallback((kind, held) => {
    if (kind === 'required') return tabForSection('family');
    if (kind === 'quarantined') {
      for (const item of held || []) {
        const tab = tabForSection(String(item.key || '').split('.')[0]);
        if (tab) return tab;
      }
    }
    return null;
  }, []);

  // Resolve a rejected payload path to something on screen. Table cells carry
  // data-col, scalar fields carry data-field. Returns null when neither
  // matches, which is what lets the button hide itself rather than no-op.
  const findErrorTarget = useCallback((kind, held) => {
    const main = document.querySelector('.main');
    if (!main) return null;
    if (kind === 'required') {
      return document.getElementById('sec-family')?.querySelector('input, select, textarea') || null;
    }
    if (kind === 'quarantined') {
      for (const item of held || []) {
        const segs = item.key.split('.');
        const leaf = segs[segs.length - 1];
        const row = segs.find(seg => /^\d+$/.test(seg));
        const cell =
          (row != null && main.querySelector(`[data-row="${row}"][data-col="${leaf}"]`)) ||
          main.querySelector(`[data-col="${leaf}"]`) ||
          main.querySelector(`[data-field="${leaf}"]`);
        if (cell) return cell.querySelector('input, select, textarea') || cell;
      }
      return null;
    }
    return main.querySelector('[aria-invalid="true"], input.date-invalid');
  }, []);

  // The button shows when the problem has a page to turn to, or is already on
  // the open page.
  const canJumpToError = useCallback((kind, held) => {
    const tab = tabForError(kind, held);
    if (tab && tab !== activeTab) return true;
    return !!findErrorTarget(kind, held);
  }, [tabForError, findErrorTarget, activeTab]);

  const jumpToError = useCallback((kind, held) => {
    const go = () => {
      const target = findErrorTarget(kind, held);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { target.focus({ preventScroll: true }); } catch { /* not focusable */ }
    };
    const tab = tabForError(kind, held);
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
      // The page's sections mount on the next render; look for the field then.
      setTimeout(go, 50);
    } else {
      go();
    }
  }, [findErrorTarget, tabForError, activeTab, setActiveTab]);

  if (loading) {
    return (
      <div className="shell">
        <div className="load-screen">
          <div className="load-spinner" />
          <p>Loading record from Salesforce…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {screen === SCREENS.PQ && (
        <div className="shell">
          <Topbar
            onPresentation={() => navigate(SCREENS.PRESENTATION)}
            onOpenConsole={() => setConsoleOpen(true)}
          />
          {restoredLocalEdits && (
            <div className="restored-banner" role="status">
              ↩ We recovered unsaved changes from your last session. They will
              sync to Salesforce automatically.
            </div>
          )}
          {coedit === 'secondary' && (
            <div className="coedit-banner" role="alert">
              ⚠ This client is already open in another tab or window. To avoid
              overwriting each other, changes here won't be saved. Edit in the
              original tab, or close it and reload this page to take over.
            </div>
          )}
          {coedit === 'conflict' && (
            <div className="coedit-banner" role="alert">
              ⚠ This client was just saved from another tab, so this page is now
              out of date. To avoid overwriting those changes, saving is paused
              here — please reload to continue from the latest version.
            </div>
          )}
          {/* Salesforce refused a save from this page because the client was
              saved from another page or device after this one opened it.
              Saving on would have written this older copy over that work,
              which is how Shelley Webster's PQ was emptied on 2026-09-21. */}
          {coedit === 'stale' && (
            <div className="coedit-banner stale-banner" role="alert">
              <span>
                ⚠ This client was saved from another tab or device after this page
                opened it, so this page has stopped saving rather than overwrite
                that work. Load the latest version to keep going. Anything typed
                here since the last save will be replaced by what is saved.
              </span>
              <button
                type="button"
                className="stale-load-latest"
                onClick={onLoadLatest}
                disabled={latestState === 'loading'}
              >
                {latestState === 'loading' ? 'Loading…' : 'Load the latest version'}
              </button>
              {latestState === 'failed' && (
                <span className="stale-load-failed">
                  Couldn't reach Salesforce. Try again in a moment.
                </span>
              )}
            </div>
          )}
          {noReference && (
            <div className="no-ref-banner" role="status">
              ⚠ This form was opened without a client reference, so your entries
              can't be saved to a household. Please open it from the client's
              record in Salesforce.
            </div>
          )}
          {loadError && (
            <div className="load-error-banner" role="alert">
              ⚠ We couldn't load this client's information right now. Please
              keep this browser tab open and submit a ticket to IT so they can
              help.
            </div>
          )}
          {(saveError || saveErrorKind === 'quarantined') && (
            <div className="save-error-banner" role="alert">
              <span className="save-error-msg">
                {saveErrorKind === 'quarantined'
                  ? saveMsgQuarantined(quarantined.map(q => q.label))
                  : saveErrorKind === 'required'
                  ? saveError
                  : saveErrorKind === 'validation'
                  ? SAVE_MSG_VALIDATION
                  : SAVE_MSG_SYSTEM}
              </span>
              {/* The reason Salesforce gave, verbatim, behind a disclosure.
                  It used to be thrown away for everything except a missing
                  name, so a save could fail for a knowable reason and the only
                  thing on screen was "some of your entries were not accepted".
                  Nobody could act on that, and nobody could report it usefully
                  either — an advisor can now open this and screenshot it
                  (Jimmy, 2026-08-26). Collapsed by default so the ordinary
                  reader sees plain guidance first. */}
              {saveError && saveErrorKind !== 'required' && (
                <details className="save-error-details">
                  <summary>What Salesforce said</summary>
                  <code>{saveError}</code>
                </details>
              )}
              {/* Only offered when there is somewhere to go. The old version
                  rendered this for any validation error and did nothing when it
                  found no target, which taught advisors to ignore the banner. */}
              {['required', 'validation', 'quarantined'].includes(saveErrorKind) &&
                canJumpToError(saveErrorKind, quarantined) && (
                <button
                  type="button"
                  className="save-error-jump"
                  onClick={() => jumpToError(saveErrorKind, quarantined)}
                >
                  Jump to the problem
                </button>
              )}
            </div>
          )}
          {householdMode && (
            <div className="household-banner" role="status">
              🏠 Opened from the household. There is no lead on this household,
              so the questionnaire saves against the household itself. The
              people and contact details come from the household record and are
              shown here rather than edited.
            </div>
          )}
          {readOnly && (
            <div className="readonly-banner" role="status">
              👁 View only. This is a read-only copy of the questionnaire, so
              nothing you do here is saved and the advisor's record can't be
              changed.
            </div>
          )}
          {/* A native disabled fieldset switches off every control inside it —
              inputs, selects, textareas and the add/delete row buttons — in one
              place, so a control added later is covered without anyone
              remembering to handle view mode. This is for clarity only; the
              actual guarantee is the pinned write gate in StoreContext. */}
          <fieldset className="form-lock" disabled={readOnly}>
            <PQScreen />
          </fieldset>
        </div>
      )}

      {screen === SCREENS.PRESENTATION && (
        <PresentationScreen onBack={() => navigate(SCREENS.PQ)} />
      )}

      {screen === SCREENS.PLAN && (
        <PlanScreen />
      )}

      {screen === SCREENS.PLANNING && (
        <PlanningScreen />
      )}

      {screen === SCREENS.ASSESSMENT && (
        REDESIGNED_ASSESSMENT
          ? <ConversationScreen onBack={() => navigate(SCREENS.PQ)} />
          : <AssessmentScreen onBack={() => navigate(SCREENS.PQ)} />
      )}

      {screen === SCREENS.ASSESSMENT_BRIEF && BriefScreen && (
        <Suspense fallback={<div className="load-screen"><p>Loading the planning brief…</p></div>}>
          <BriefScreen onBack={() => navigate(SCREENS.ASSESSMENT)} />
        </Suspense>
      )}

      {import.meta.env.DEV && (
        <DataConsole open={consoleOpen} onClose={() => setConsoleOpen(false)} />
      )}
      <UndoToast />
    </>
  );
}

export default function App() {
  if (FOUNDATION_ENABLED && FOUNDATION_SCREENS.includes(screenFromPath(window.location.pathname))) {
    return <FoundationShell />;
  }
  return (
    <StoreProvider>
      <LayoutProvider>
        <AppShell />
      </LayoutProvider>
    </StoreProvider>
  );
}
